import { useIsMobile } from "../../hooks/useIsMobile";
import React, { useEffect, useState, useRef } from "react";
import Alert from "../../common/layouts/modal/Alert";
import AuthorPage from "./AuthorPage";
import { useLocation, useParams } from "react-router-dom";
import { getAuthorInfo, getBookList, getLike } from "../../api";
import confetti from 'canvas-confetti';
import { useLoading } from "../../common/controls/LoadingContext";
import usePageTitle from "../../hooks/usePageTitle";
import { trackEvent } from '../../lib/analytics';

interface Author {
    authorName: string;
    authorKeyword?: string;
    authorIntro?: string;
    authorEmail?: string;
    authorDescription?: string;
}

const AuthorDetailContainer = () => {
    const isMobile = useIsMobile();
    const { showLoading, hideLoading } = useLoading();
    const [authors, setAuthors] = useState<Author | null>(null);
    const { authorId: authorIdParam } = useParams();
    const location = useLocation();
    const [authorData, setAuthorData] = useState<string | null>(null); // This stores authorEmail if available
    const [authorIdInfo, setAuthorIdInfo] = useState<number | null>(null); // This stores authorId
    const [authorNameInfo, setAuthorNameInfo] = useState<string | null>(null); // This stores authorName
    const [bookData, setBookData] = useState<any[]>([]);
    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    let stateEmail = '';
    const [followAlert, setFollowAlert] = useState(false);
    const [showDeactivatedAlert, setShowDeactivatedAlert] = useState(false);
    const viewedRef = useRef('');

    useEffect(() => {
        if (!authorData) return;

        if (viewedRef.current === authorData) return;
        viewedRef.current = authorData;

        trackEvent('view_author_detail', {
            author_id: authorData,
            page_path: window.location.pathname,
        });
    }, [authorData]);


    useEffect(() => {
        const emailFromState = location.state?.authorEmail;
        const idFromParam = authorIdParam;

        if (emailFromState) {
            setAuthorData(emailFromState);
        }
        
        if (idFromParam) {
            const num = Number(idFromParam);
            if (!isNaN(num)) {
                setAuthorIdInfo(num);
                setAuthorNameInfo(null);
            } else {
                setAuthorIdInfo(null);
                setAuthorNameInfo(idFromParam);
            }
        }
    }, [location.state, authorIdParam]);

    useEffect(() => {
        const getData = async () => {
            if (authorData != null || authorIdInfo != null || authorNameInfo != null) {
                showLoading();

                try {
                    const data = await getAuthorInfo({ 
                        authorEmail: authorData || undefined, 
                        authorId: authorIdInfo || undefined,
                        authorName: authorNameInfo || undefined
                      });
                    if (typeof data.like === 'boolean') {
                        setIsFollowing(data.like);
                    }
                    setAuthors(data);
                    // 조회 성공 시 이메일 업데이트 (직접 접속 시 이름으로 들어온 경우 대응)
                    if (data.authorEmail && !authorData) {
                        setAuthorData(data.authorEmail);
                    }
                } catch (error) {
                    // console.error("Author info loading error:", error);
                } finally {
                    hideLoading();
                }
            }
        };

        getData();
    }, [authorData, authorIdInfo, authorNameInfo]);

    useEffect(() => {
        const getBookData = async () => {
            if (authorData != null) {
                const data = await getBookList(authorData);
                setBookData(data);
            }
        };

        getBookData();
    }, [authorData]);

    const handleFollowToggle = async () => {
        if (!authorData) return;

        const authorStatus: string | null = localStorage.getItem("authorStatus")

        const authorYn: boolean = !!authorStatus; //로그인이 안 되어 있는 경우

        if (!authorYn) {
            setFollowAlert(true);
            return;
        }

        if (authorStatus && authorStatus.toLowerCase() === "hold") {
            setShowDeactivatedAlert(true);
            return;
        }

        const nextFollow = !isFollowing;
        setIsFollowing(nextFollow);

        await getLike(authorData);
    };


    const safeAuthors = authors || {
        authorName: "",
        authorKeyword: "",
        authorIntro: "",
        authorEmail: "",
        authorDescription: "",
    };

    usePageTitle(
        safeAuthors.authorName ? `${safeAuthors.authorName} | 북밋` : "북밋 작가 상세페이지",
        safeAuthors.authorName ? `${safeAuthors.authorName} 작가의 작품과 엽서를 북밋에서 만나보세요.` : undefined,
        !!safeAuthors.authorName
    );

    return (
        <>
            <AuthorPage
                authors={safeAuthors}
                bookData={bookData}
                onFollowToggle={handleFollowToggle}
                isFollowing={isFollowing}
                followAlert={followAlert}
                setFollowAlert={setFollowAlert}
            />
            {showDeactivatedAlert && (
                <Alert
                    title="알림"
                    message={`현재 계정 이용이 제한된 상태입니다.\n운영 정책 위반으로 인해\n팔로우 기능이 제한되었습니다.\n기존 게시글은 수정 후 안내 메일에 따라 재검토 요청이 가능합니다.`}
                    onClose={() => setShowDeactivatedAlert(false)}
                />
            )}
        </>
    );
};

export default AuthorDetailContainer;
